'use client';

import { c as _c } from "react/compiler-runtime";
import type { FileReviewDecision } from '@/lib/pr-v2/file-review-decisions';
import type { CodeContent } from '@/types/github';
import type { ReactElement } from 'react';
import { PrV2DecisionBadge } from '../decisions/pr-v2-decision-badge';
import { getFileChangeCounts } from './pr-v2-file-story-metadata-utils';
import { PrV2GroupNavigation, PrV2GroupReviewedControl } from './pr-v2-group-controls';
import { PrV2GroupDescription } from './pr-v2-group-description';
import { PrV2GroupFileList } from './pr-v2-group-file-list';
import { DiffLineStats } from '@/components/ui/diff-line-stats';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCountedNoun } from '@/lib/auto-fix-loop/outcome-copy';
import { formatFileReviewDecisionLabel, getFileReviewDecisionGlyphChar } from '@/lib/pr-v2/file-review-decisions';
export function PrV2GroupMeta(t0) {
  const $ = _c(37);
  const {
    sectionIndex,
    sectionCount,
    showIndex,
    files,
    decisions
  } = t0;
  let additions = 0;
  let deletions = 0;
  if ($[0] !== additions || $[1] !== deletions || $[2] !== files) {
    for (const file of files) {
      const counts = getFileChangeCounts(file);
      additions = additions + counts.additionsCount;
      deletions = deletions + counts.deletionsCount;
    }
    $[0] = additions;
    $[1] = deletions;
    $[2] = files;
    $[3] = additions;
    $[4] = deletions;
  } else {
    additions = $[3];
    deletions = $[4];
  }
  let t1;
  if ($[5] !== decisions) {
    t1 = formatFileReviewDecisionLabel(decisions);
    $[5] = decisions;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const decisionLabel = t1;
  let items;
  if ($[7] !== additions || $[8] !== decisionLabel || $[9] !== decisions || $[10] !== deletions || $[11] !== files.length || $[12] !== sectionCount || $[13] !== sectionIndex || $[14] !== showIndex) {
    items = [];
    if (showIndex && sectionCount > 1) {
      const t2 = `pr-v2-group-index-${sectionIndex}`;
      const t3 = `Group ${sectionIndex} of ${sectionCount}`;
      let t4;
      if ($[16] !== t2 || $[17] !== t3) {
        t4 = <span key="index" data-testid={t2}>{t3}</span>;
        $[16] = t2;
        $[17] = t3;
        $[18] = t4;
      } else {
        t4 = $[18];
      }
      items.push(t4);
    }
    let t2;
    if ($[19] !== files.length) {
      t2 = formatCountedNoun(files.length, "file");
      $[19] = files.length;
      $[20] = t2;
    } else {
      t2 = $[20];
    }
    let t3;
    if ($[21] !== t2) {
      t3 = <span key="files">{t2}</span>;
      $[21] = t2;
      $[22] = t3;
    } else {
      t3 = $[22];
    }
    items.push(t3);
    if (additions > 0 || deletions > 0) {
      let t4;
      if ($[23] !== additions || $[24] !== deletions) {
        t4 = <DiffLineStats key="lines" additions={additions} deletions={deletions} />;
        $[23] = additions;
        $[24] = deletions;
        $[25] = t4;
      } else {
        t4 = $[25];
      }
      items.push(t4);
    }
    if (decisionLabel) {
      let t4;
      if ($[26] !== decisions) {
        t4 = getFileReviewDecisionGlyphChar(decisions);
        $[26] = decisions;
        $[27] = t4;
      } else {
        t4 = $[27];
      }
      let t5;
      if ($[28] !== decisionLabel || $[29] !== decisions.length || $[30] !== t4) {
        t5 = <PrV2DecisionBadge key="decisions" label={decisionLabel} glyph={t4} count={decisions.length} />;
        $[28] = decisionLabel;
        $[29] = decisions.length;
        $[30] = t4;
        $[31] = t5;
      } else {
        t5 = $[31];
      }
      items.push(t5);
    }
    $[7] = additions;
    $[8] = decisionLabel;
    $[9] = decisions;
    $[10] = deletions;
    $[11] = files.length;
    $[12] = sectionCount;
    $[13] = sectionIndex;
    $[14] = showIndex;
    $[15] = items;
  } else {
    items = $[15];
  }
  const t2 = `pr-v2-group-meta-${sectionIndex}`;
  let t3;
  if ($[32] !== items) {
    t3 = items.map((item, index) => <span key={item.key} className="inline-flex items-center gap-1.5 whitespace-nowrap">{item}{index < items.length - 1 ? <span aria-hidden="true">·</span> : null}</span>);
    $[32] = items;
    $[33] = t3;
  } else {
    t3 = $[33];
  }
  let t4;
  if ($[34] !== t2 || $[35] !== t3) {
    t4 = <div data-testid={t2} className="flex flex-wrap items-center gap-x-1.5 text-[12px] leading-5 tabular-nums text-zinc-500">{t3}</div>;
    $[34] = t2;
    $[35] = t3;
    $[36] = t4;
  } else {
    t4 = $[36];
  }
  return t4;
}
export function PrV2GroupingStatus(t0) {
  const $ = _c(8);
  const {
    sectionIndex,
    fileCount
  } = t0;
  const t1 = `pr-v2-group-grouping-${sectionIndex}`;
  let t2;
  if ($[0] !== fileCount) {
    t2 = formatCountedNoun(fileCount, "file");
    $[0] = fileCount;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `Grouping ${t2}…`;
  let t4;
  if ($[2] !== t3) {
    t4 = <span className="block text-[13px] text-zinc-300">{t3}</span>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="space-y-2" aria-hidden="true"><Skeleton className="h-2 w-5/6 bg-zinc-800" /><Skeleton className="h-2 w-2/3 bg-zinc-800" /><Skeleton className="h-2 w-3/4 bg-zinc-800" /></div>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t1 || $[6] !== t4) {
    t6 = <div role="status" data-testid={t1} className="space-y-3">{t4}{t5}</div>;
    $[5] = t1;
    $[6] = t4;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}
export type PrV2LeftGroupHeaderProps = {
  sectionIndex: number;
  sectionCount: number;
  groupId: string;
  groupName: string;
  groupNameReason?: string;
  files: CodeContent[];
  decisions: readonly FileReviewDecision[];
  /** AI grouping is still running, so the group is the "Changed files" placeholder. */
  isGroupingPending: boolean;
  activeFilename: string | null;
  isGroupReviewed: boolean;
  onSelectFile: (filename: string) => void;
  onToggleGroupReviewed?: () => void;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
};
export function PrV2LeftGroupHeader(t0) {
  const $ = _c(21);
  const {
    sectionIndex,
    sectionCount,
    groupName,
    groupNameReason,
    files,
    decisions,
    isGroupingPending,
    activeFilename,
    isGroupReviewed,
    onSelectFile,
    onToggleGroupReviewed,
    onNavigatePrevious,
    onNavigateNext
  } = t0;
  const t1 = `pr-v2-group-guide-${sectionIndex}`;
  let t2;
  if ($[0] !== decisions || $[1] !== files || $[2] !== groupName || $[3] !== groupNameReason || $[4] !== isGroupReviewed || $[5] !== isGroupingPending || $[6] !== onNavigateNext || $[7] !== onNavigatePrevious || $[8] !== onToggleGroupReviewed || $[9] !== sectionCount || $[10] !== sectionIndex) {
    t2 = isGroupingPending ? <PrV2GroupingStatus sectionIndex={sectionIndex} fileCount={files.length} /> : <><div className="space-y-2"><h2 className="text-base font-medium leading-snug text-foreground @min-[64rem]/pr-diff:text-[18px]">{groupName}</h2><PrV2GroupDescription groupNameReason={groupNameReason} /><PrV2GroupMeta sectionIndex={sectionIndex} sectionCount={sectionCount} showIndex={true} files={files} decisions={decisions} /></div><div className="flex items-center justify-between gap-2 text-[12px]"><PrV2GroupReviewedControl sectionIndex={sectionIndex} groupName={groupName} isGroupReviewed={isGroupReviewed} onToggleGroupReviewed={onToggleGroupReviewed} />{sectionCount > 1 ? <PrV2GroupNavigation onNavigatePrevious={onNavigatePrevious} onNavigateNext={onNavigateNext} className="ml-auto" /> : null}</div></>;
    $[0] = decisions;
    $[1] = files;
    $[2] = groupName;
    $[3] = groupNameReason;
    $[4] = isGroupReviewed;
    $[5] = isGroupingPending;
    $[6] = onNavigateNext;
    $[7] = onNavigatePrevious;
    $[8] = onToggleGroupReviewed;
    $[9] = sectionCount;
    $[10] = sectionIndex;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== activeFilename || $[13] !== files || $[14] !== groupName || $[15] !== onSelectFile) {
    t3 = <PrV2GroupFileList groupName={groupName} files={files} activeFilename={activeFilename} onSelectFile={onSelectFile} className="hidden @min-[64rem]/pr-diff:block" />;
    $[12] = activeFilename;
    $[13] = files;
    $[14] = groupName;
    $[15] = onSelectFile;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== t1 || $[18] !== t2 || $[19] !== t3) {
    t4 = <div className="space-y-4" data-testid={t1} data-group-guide-placement="left">{t2}{t3}</div>;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  return t4;
}